(() => {
  'use strict';

  if (window.customElements.get('collection-row-filler')) {
    return;
  }

  const ITEM_SELECTOR = 'product-card, .product-list__promo';

  class CollectionRowFiller extends HTMLElement {
    connectedCallback() {
      this.list = this.closest('.collection__results')?.querySelector('product-list');

      if (!this.list || !this.getAttribute('next-url')) {
        return;
      }

      this.addedCards = [];
      this.spareCards = null;
      this.nextPageIsLast = false;
      this.filledColumnCount = null;
      this.fillId = 0;

      this.resizeObserver = new ResizeObserver(() => this.onLayoutChange());
      this.resizeObserver.observe(this.list);

      const firstCard = this.list.querySelector('product-card');

      if (firstCard) {
        this.resizeObserver.observe(firstCard);
      }

      this.onLayoutChange();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      clearTimeout(this.layoutTimeout);
    }

    get pagination() {
      return this.closest('.collection')?.querySelector('.pagination');
    }

    onLayoutChange() {
      clearTimeout(this.layoutTimeout);

      this.layoutTimeout = setTimeout(() => {
        if (this.columnCount !== this.filledColumnCount) {
          this.fill();
        }
      }, 100);
    }

    get columnCount() {
      const tracks = getComputedStyle(this.list).gridTemplateColumns.trim();
      return tracks && tracks !== 'none' ? tracks.split(/\s+/).length : 1;
    }

    get items() {
      return Array.from(this.list.children).filter((element) => element.matches(ITEM_SELECTOR));
    }

    lastRowBottom(items = this.items) {
      return Math.max(0, ...items.map((element) => element.offsetTop + element.offsetHeight));
    }

    lastRowTop(items = this.items) {
      return Math.max(0, ...items.map((element) => element.offsetTop));
    }

    emptyCellCount() {
      const columns = this.columnCount;

      if (columns < 2) {
        return 0;
      }

      const items = this.items;

      if (items.length === 0) {
        return 0;
      }

      const bottom = this.lastRowBottom(items);
      let occupied = 0;

      items.forEach((element) => {
        if (Math.abs(element.offsetTop + element.offsetHeight - bottom) > 1) {
          return;
        }

        const span = getComputedStyle(element).gridColumnEnd.match(/span\s+(\d+)/);
        occupied += span ? parseInt(span[1], 10) : 1;
      });

      return occupied >= columns ? 0 : columns - occupied;
    }

    async fill() {
      const fillId = ++this.fillId;
      const columns = this.columnCount;

      this.addedCards.forEach((card) => card.remove());
      this.addedCards = [];
      this.filledColumnCount = columns;
      this.updatePagination();

      if (this.emptyCellCount() === 0) {
        return;
      }

      const spareCards = await this.loadSpareCards();

      if (!this.isConnected || fillId !== this.fillId) {
        return;
      }

      for (const card of spareCards) {
        if (this.emptyCellCount() === 0) {
          break;
        }

        // Compare row tops, not bottoms: a card with a longer title can make the last row taller
        // without wrapping, which would otherwise discard it and leave the row incomplete.
        const topBefore = this.lastRowTop();
        this.list.appendChild(card);

        if (card.offsetTop > topBefore + 1) {
          card.remove();
          continue;
        }

        this.addedCards.push(card);
      }

      this.updatePagination();
    }

    updatePagination() {
      const pagination = this.pagination;

      if (!pagination) {
        return;
      }

      const absorbedNextPage = this.nextPageIsLast && this.spareCards?.length > 0 && this.addedCards.length === this.spareCards.length;
      pagination.toggleAttribute('hidden', absorbedNextPage);
    }

    async loadSpareCards() {
      if (this.spareCards) {
        return this.spareCards;
      }

      const url = new URL(this.getAttribute('next-url'), window.location.origin);
      url.searchParams.set('section_id', this.getAttribute('section-id'));

      try {
        const response = await fetch(url.toString());
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        this.nextPageIsLast = !doc.querySelector('.pagination a[rel="next"]');
        this.spareCards = Array.from(doc.querySelectorAll('product-list > product-card')).map((card) => {
          card.removeAttribute('reveal-on-scroll');
          card.setAttribute('data-row-filler', '');
          return document.importNode(card, true);
        });
      } catch (error) {
        this.spareCards = [];
      }

      return this.spareCards;
    }
  }

  window.customElements.define('collection-row-filler', CollectionRowFiller);
})();
